import React, { useEffect, useState, useCallback } from "react";
import { maintenanceApi, AuditLogEntry } from "../../../api/maintenance";
import MaintenanceLayout from "../shared/MaintenanceLayout";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";

const AuditLog: React.FC = () => {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [entityType, setEntityType] = useState("");
  const [action, setAction] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, page_size: pageSize };
      if (entityType) params.entity_type = entityType;
      if (action) params.action = action;
      const res = await maintenanceApi.getAuditLogs(params);
      setLogs(res.data.items);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch (err) {
      console.error("Failed to fetch audit logs:", err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, entityType, action]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const formatChanges = (changes: Record<string, any> | null): string => {
    if (!changes) return "-";
    try {
      return Object.entries(changes)
        .map(([key, val]) => {
          if (val && typeof val === "object" && "old" in val && "new" in val) {
            return `${key}: ${val.old} -> ${val.new}`;
          }
          return `${key}: ${JSON.stringify(val)}`;
        })
        .join(", ");
    } catch {
      return JSON.stringify(changes);
    }
  };

  return (
    <MaintenanceLayout title="Audit Log">
      <div className="maint-stations">
        <div className="maint-toolbar">
          <div className="maint-filters">
            <select value={entityType} onChange={(e) => { setEntityType(e.target.value); setPage(1); }}>
              <option value="">All Types</option>
              <option value="station">Station</option>
              <option value="device">Device</option>
              <option value="connector">Connector</option>
            </select>
            <select value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }}>
              <option value="">All Actions</option>
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
              <option value="override_status">Override Status</option>
            </select>
          </div>
        </div>

        {loading ? (
          <p className="maint-loading">Loading...</p>
        ) : (
          <>
            <table className="maint-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Action</th>
                  <th>Type</th>
                  <th>Entity ID</th>
                  <th>Changes</th>
                  <th>User</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td>{new Date(log.performed_at).toLocaleString()}</td>
                    <td><span className={`maint-action maint-action-${log.action}`}>{log.action}</span></td>
                    <td>{log.entity_type}</td>
                    <td className="maint-mono">{log.entity_id.substring(0, 12)}...</td>
                    <td className="maint-changes">{formatChanges(log.changes)}</td>
                    <td>{log.performed_by_email || log.performed_by.substring(0, 8) + "..."}</td>
                    <td>{log.notes || "-"}</td>
                  </tr>
                ))}
                {logs.length === 0 && (
                  <tr><td colSpan={7} className="maint-empty">No audit logs found</td></tr>
                )}
              </tbody>
            </table>

            <div className="maint-pagination">
              <span>{total} entries total</span>
              <div className="maint-pagination-controls">
                <button disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft size={16} /></button>
                <span>Page {page} of {totalPages}</span>
                <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}><ChevronRight size={16} /></button>
              </div>
            </div>
          </>
        )}
      </div>
    </MaintenanceLayout>
  );
};

export default AuditLog;
